import { Layout } from 'react-grid-layout'

import { IDashboardPreferences } from '~/client/graph/types.generated'

import { getDefaultLayout } from '../Constants/WidgetConfig'

const LAYOUT_STORAGE_KEY = 'analytics_dashboard_layout'
const VISIBILITY_STORAGE_KEY = 'analytics_dashboard_widget_visibility'
const CHART_TYPE_STORAGE_KEY = 'analytics_dashboard_widget_chart_types'

export interface DashboardPreferencesData {
  layout?: Layout[]
  widgetVisibility?: Record<string, boolean>
  chartTypes?: Record<string, string>
}

export class LayoutManager {
  // Callback for when preferences change (will be set by HubV2)
  private static onPreferencesChange?: (prefs: DashboardPreferencesData) => void

  public static setPreferencesChangeCallback(
    callback: (prefs: DashboardPreferencesData) => void,
  ): void {
    this.onPreferencesChange = callback
  }

  // Initialize from backend preferences
  public static initializeFromBackend(
    backendPrefs: IDashboardPreferences | null | undefined,
  ): void {
    if (!backendPrefs) return

    try {
      // Load layout from backend if available
      if (backendPrefs.layout) {
        const layout = JSON.parse(backendPrefs.layout)
        localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(layout))
      }

      // Load widget visibility from backend if available
      if (backendPrefs.widgetVisibility) {
        const visibility = JSON.parse(backendPrefs.widgetVisibility)
        localStorage.setItem(VISIBILITY_STORAGE_KEY, JSON.stringify(visibility))
      }

      // Load chart types from backend if available
      if (backendPrefs.chartTypes) {
        const chartTypes = JSON.parse(backendPrefs.chartTypes)
        localStorage.setItem(CHART_TYPE_STORAGE_KEY, JSON.stringify(chartTypes))
      }
    } catch (error) {
      console.error('Failed to initialize from backend preferences:', error)
    }
  }

  // Get current preferences for backend sync
  public static getCurrentPreferences(): DashboardPreferencesData {
    return {
      layout: this.loadLayout() || undefined,
      widgetVisibility: this.loadWidgetVisibility(),
      chartTypes: this.loadWidgetChartTypes(),
    }
  }

  // Notify backend of changes
  private static notifyPreferencesChange(): void {
    if (this.onPreferencesChange) {
      this.onPreferencesChange(this.getCurrentPreferences())
    }
  }

  public static saveLayout(layout: Layout[]): void {
    try {
      localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(layout))
      this.notifyPreferencesChange()
    } catch (error) {
      console.error('Failed to save layout:', error)
    }
  }

  public static loadLayout(): Layout[] | null {
    try {
      const saved = localStorage.getItem(LAYOUT_STORAGE_KEY)
      return saved ? JSON.parse(saved) : null
    } catch (error) {
      console.error('Failed to load layout:', error)
      return null
    }
  }

  public static getLayout(): Layout[] {
    const saved = this.loadLayout()
    const defaults = getDefaultLayout()

    if (!saved) return defaults

    return defaults.map(defaultItem => {
      const savedItem = saved.find(s => s.i === defaultItem.i)
      return savedItem || defaultItem
    })
  }

  public static resetLayout(): Layout[] {
    try {
      localStorage.removeItem(LAYOUT_STORAGE_KEY)
      this.notifyPreferencesChange()
      return getDefaultLayout()
    } catch (error) {
      console.error('Failed to reset layout:', error)
      return getDefaultLayout()
    }
  }

  public static saveWidgetVisibility(
    widgetId: string,
    isVisible: boolean,
  ): void {
    try {
      const visibility = this.loadWidgetVisibility()
      visibility[widgetId] = isVisible
      localStorage.setItem(VISIBILITY_STORAGE_KEY, JSON.stringify(visibility))
      this.notifyPreferencesChange()
    } catch (error) {
      console.error('Failed to save widget visibility:', error)
    }
  }

  public static loadWidgetVisibility(): Record<string, boolean> {
    try {
      const saved = localStorage.getItem(VISIBILITY_STORAGE_KEY)
      return saved ? JSON.parse(saved) : {}
    } catch (error) {
      console.error('Failed to load widget visibility:', error)
      return {}
    }
  }

  public static resetWidgetVisibility(): void {
    try {
      localStorage.removeItem(VISIBILITY_STORAGE_KEY)
      this.notifyPreferencesChange()
    } catch (error) {
      console.error('Failed to reset widget visibility:', error)
    }
  }

  public static saveWidgetChartType(widgetId: string, chartType: string): void {
    try {
      const chartTypes = this.loadWidgetChartTypes()
      chartTypes[widgetId] = chartType
      localStorage.setItem(CHART_TYPE_STORAGE_KEY, JSON.stringify(chartTypes))
      this.notifyPreferencesChange()
    } catch (error) {
      console.error('Failed to save widget chart type:', error)
    }
  }

  public static loadWidgetChartTypes(): Record<string, string> {
    try {
      const saved = localStorage.getItem(CHART_TYPE_STORAGE_KEY)
      return saved ? JSON.parse(saved) : {}
    } catch (error) {
      console.error('Failed to load widget chart types:', error)
      return {}
    }
  }

  public static resetWidgetChartTypes(): void {
    try {
      localStorage.removeItem(CHART_TYPE_STORAGE_KEY)
      this.notifyPreferencesChange()
    } catch (error) {
      console.error('Failed to reset widget chart types:', error)
    }
  }
}
